import React, { useEffect, useRef, useState, useCallback } from "react";
import { EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import { motion, AnimatePresence } from "motion/react";
import Resize from "@/assets/icons/Resize.svg";
import MuteSm from "@/assets/icons/MuteSm.svg";
import Volume from "@/assets/icons/Volume.svg";
import ArrowsSkip from "@/assets/icons/ArrowsSkip.svg";
import PlayBlack from "@/assets/icons/PlayBlack.svg";
import VideoLoader from "@/assets/icons/VideoLoader.svg";
import MediaSliderFull from "./MediaSliderFull";
import { ScreenshotWithComments } from "./ScreenshotWithComments";
import { profilePictures } from "@/lib/data";

interface ElementComment {
  url: string;
  selector: string;
  xpath?: string;
  offsetX: number;
  offsetY: number;
  fallbackX?: number;
  fallbackY?: number;
  text: string;
  commentNumber: number;
  createdAt: Date;
  updatedAt: Date;
  textContent?: string;
  directText?: string;
  attributes?: Record<string, string>;
  styles?: {
    color?: string;
    backgroundColor?: string;
    fontSize?: string;
    fontFamily?: string;
  };
  innerHTML?: string;
  outerHTML?: string;
  elementWidth?: number;
  elementHeight?: number;
  screenshot?: string;
  screenshotX?: number;
  screenshotY?: number;
  screenshotXPercent?: number;
  screenshotYPercent?: number;
  username?: string;
  images?: string[];
  profilePicture?: number;
}

type PropType = {
  slides: {
    mediaType: string;
    url: string;
    filename?: string;
    elementComment?: ElementComment;
    elementComments?: ElementComment[];
  }[];
  options?: EmblaOptionsType;
  mediaType: "image" | "video" | "dom-replay" | "comments" | undefined;
  report?: any;
};

type VideoState = {
  duration: number;
  currentTime: number;
  isPlaying: boolean;
  volume: number;
  isMuted: boolean;
};

const MediaSlider: React.FC<PropType> = (props) => {
  const { slides, options, mediaType, report } = props;
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const wheelContainerRef = useRef<HTMLDivElement | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenSlides, setFullscreenSlides] = useState(slides);
  const [fullscreenStartIndex, setFullscreenStartIndex] = useState(0);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    ...options,
    axis: isMobile ? "x" : "y",
  });
  const [currentIndex, setCurrentIndex] = useState(0);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const normalContainerRef = useRef<HTMLDivElement | null>(null);
  // const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const skipAccumulatorRef = useRef<{
    amount: number;
    timeout: NodeJS.Timeout | null;
  }>({ amount: 0, timeout: null });
  const [videoState, setVideoState] = useState<VideoState>({
    duration: 0,
    currentTime: 0,
    isPlaying: false,
    volume: 1,
    isMuted: false,
  });
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);
  const animationFrameRef = useRef<number>();
  const [hoverPosition, setHoverPosition] = useState<{
    percentage: number;
    time: number;
  } | null>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [canPlay, setCanPlay] = useState(false);
  const [isWaitingForData, setIsWaitingForData] = useState(false);
  const [videoWidth, setVideoWidth] = useState<number>(0);
  const [isTimelineVisible, setIsTimelineVisible] = useState(true);
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isHoveringControls, setIsHoveringControls] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrentIndex(emblaApi.selectedScrollSnap());
    // Reset video states when switching slides
    setCanPlay(false);
    setVideoLoaded(false);
    setIsWaitingForData(false);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  const handlePrevClick = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const handleNextClick = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const handleOpenNewImages = useCallback((images: string[], startIndex: number) => {
    const imageSlides = images.map((url) => ({ mediaType: "image", url }));
    setFullscreenSlides(imageSlides);
    setFullscreenStartIndex(startIndex);
    setIsFullscreen(true);
  }, []);

  const prevBtnDisabled = !emblaApi || currentIndex === 0;
  const nextBtnDisabled = !emblaApi || currentIndex === slides.length - 1;

  const togglePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      if (video.paused) {
        video.play().catch((err) => {
          console.error(`Error playing video:`, err);
        });
      } else {
        video.pause();
      }
    }
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      const newMutedState = !video.muted;
      video.muted = newMutedState;

      // Set volume to 0 when muting, restore to 1 when unmuting
      const newVolume = newMutedState ? 0 : 1;
      video.volume = newVolume;

      setVideoState((prev) => ({
        ...prev,
        isMuted: newMutedState,
        volume: newVolume,
      }));
    }
  }, []);

  const handleVolumeChange = useCallback((newVolume: number) => {
    const video = videoRef.current;
    if (video) {
      const clampedVolume = Math.max(0, Math.min(1, newVolume));
      video.volume = clampedVolume;
      video.muted = clampedVolume === 0;
      setVideoState((prev) => ({
        ...prev,
        volume: clampedVolume,
        isMuted: clampedVolume === 0,
      }));
    }
  }, []);

  const skipVideo = useCallback(
    (seconds: number) => {
      const video = videoRef.current;
      if (video && mediaType === "video") {
        const duration = video.duration;
        const currentTime = video.currentTime || 0;
        let newTime = currentTime;
        let didSkip = false;

        // Only skip if duration is valid
        if (isFinite(duration) && duration > 0) {
          // Don't allow backward skip if we're within 0.01 seconds of the start
          // if (seconds < 0 && currentTime < 0.1) {
          //   return;
          // }

          // Calculate new time and strictly enforce bounds
          newTime = currentTime + seconds;

          // Clamp to valid range [0, duration]
          newTime = Math.max(0, Math.min(duration, newTime));

          // Ensure it's a finite number
          if (!isFinite(newTime)) {
            newTime = 0;
          }

          // Only update if the new time is different and valid
          if (newTime !== currentTime && newTime >= 0 && newTime <= duration) {
            try {
              video.currentTime = newTime;
              setVideoState((prev) => ({
                ...prev,
                currentTime: newTime,
              }));
              didSkip = true;
            } catch (e) {
              // Ignore errors from setting currentTime
              console.warn("Failed to set video time:", e);
              return; // Don't show indicator if we couldn't skip
            }
          }
        }

        // Accumulate skip amount
        const skipData = skipAccumulatorRef.current;

        // If direction changed or this is the first skip, reset accumulator
        if (
          (skipData.amount > 0 && seconds < 0) ||
          (skipData.amount < 0 && seconds > 0)
        ) {
          skipData.amount = 0;
        }

        // Calculate the actual skip amount (how much we actually moved)
        const actualSkipAmount = newTime - currentTime;

        // If we didn't move at all (stuck at boundary), don't show or update indicator
        if (!didSkip || actualSkipAmount === 0) {
          // If we're stuck at the boundary and already have an accumulator, keep showing it
          if (skipData.amount !== 0) {
            // Don't change the accumulator, just show what we have
          } else {
            // No movement and no accumulator - don't show anything
            return;
          }
        } else {
          // We moved - check if we moved the full amount
          const movedFullAmount =
            Math.abs(Math.abs(actualSkipAmount) - Math.abs(seconds)) < 0.1;

          if (movedFullAmount) {
            skipData.amount += seconds;
          } else if (skipData.amount === 0) {
            // First skip that hit a boundary - show the standard amount
            skipData.amount = seconds;
          }
          // Otherwise don't change the accumulator (keeps showing the same amount)
        }

        // Clear existing timeout
        if (skipData.timeout) {
          clearTimeout(skipData.timeout);
        }

        // Show skip indicator using DOM manipulation - append to body to avoid React conflicts
        // Remove existing indicator if any
        // const existing = document.querySelector("[data-skip-indicator]");
        // if (existing) {
        //   existing.remove();
        // }

        const existingIndicators = document.querySelectorAll(
          "[data-skip-indicator]",
        );
        existingIndicators.forEach((ind) => {
          try {
            ind.remove();
          } catch (e) {
            // ignore
          }
        });

        let leftPos;
        let topPos;
        const containerElement = isFullscreen
          ? normalContainerRef.current || document.body
          : document.body;

        try {
          if (isFullscreen) {
            // In fullscreen, position relative to viewport
            leftPos = skipData.amount < 0 ? 56 : window.innerWidth - 56;
            topPos = window.innerHeight / 2;
          } else {
            // In normal mode, position relative to video
            const videoRect = video.getBoundingClientRect();
            if (videoRect.width === 0 || videoRect.height === 0) {
              return;
            }
            leftPos =
              skipData.amount < 0 ? videoRect.left + 56 : videoRect.right - 56;
            topPos = videoRect.top + videoRect.height / 2;
          }
        } catch (e) {
          console.warn("Failed to calculate indicator position:", e);
          return;
        }

        // Create new indicator
        const indicator = document.createElement("div");
        indicator.setAttribute("data-skip-indicator", "true");

        indicator.style.cssText = `
        position: ${isFullscreen ? "absolute" : "fixed"};
        left: ${leftPos}px;
        top: ${topPos}px;
        transform: translate(${skipData.amount < 0 ? "0" : "-100%"}, -50%);
        pointer-events: none;
        z-index: 999999999999999;
        display: flex;
        align-items: center;
        gap: 16px;
        opacity: 0;
        transition: opacity 0.2s ease-in-out;
        ${skipData.amount > 0 ? "flex-direction: row-reverse;" : ""}
      `;

        // Create chevron arrow using ArrowsSkip image (reversed direction)
        const chevron = document.createElement("img");
        chevron.src = ArrowsSkip;
        chevron.alt = "Skip";
        chevron.style.cssText = `
          ${skipData.amount > 0 ? "transform: scaleX(-1);" : ""}
          filter: drop-shadow(0px 0px 16px rgba(0, 0, 0, 0.6));
        `;

        // Create text element
        const text = document.createElement("div");
        text.textContent = `${skipData.amount > 0 ? "+" : "-"}${Math.abs(
          skipData.amount,
        )}`;
        text.style.cssText = `
          color: white;
          font-weight: 500;
          font-family: "Onest", sans-serif;
          font-size: 24px;
          line-height: 1;
          filter: drop-shadow(0px 0px 16px rgba(0, 0, 0, 0.6));
          text-align: center;
        `;

        indicator.appendChild(chevron);
        indicator.appendChild(text);
        containerElement.appendChild(indicator);

        // Trigger fade in
        requestAnimationFrame(() => {
          indicator.style.opacity = "1";
        });

        // Set timeout to reset accumulator and fade out indicator
        skipData.timeout = setTimeout(() => {
          indicator.style.opacity = "0";
          setTimeout(() => {
            indicator.remove();
          }, 200);

          // Reset accumulator
          skipData.amount = 0;
          skipData.timeout = null;
        }, 500);
      }
    },
    [mediaType, isFullscreen],
  );

  useEffect(() => {
    function handleKeyDown(event: any) {
      // Don't trigger if user is typing in an input or editable element
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.isContentEditable ||
        event.target.getAttribute("contenteditable") === "true" ||
        event.target.closest("input") ||
        event.target.closest("textarea") ||
        event.target.closest('[contenteditable="true"]')
      ) {
        return;
      }

      if (event.key === "Escape" && isFullscreen) {
        event.stopPropagation();
        setIsFullscreen(false);
        return;
      }

      // Spacebar to play/pause video
      if (event.key === " " && mediaType === "video") {
        event.preventDefault();
        event.stopPropagation();
        togglePlayPause();
        return;
      }

      // Arrow keys for video skip (left/right) or image navigation (up/down on desktop, left/right on mobile)
      if (mediaType === "video") {
        if (event.key === "ArrowRight") {
          event.preventDefault();
          event.stopPropagation();
          skipVideo(5);
        } else if (event.key === "ArrowLeft") {
          event.preventDefault();
          event.stopPropagation();
          skipVideo(-5);
        }
      } else {
        // Image navigation
        if (isMobile) {
          if (event.key === "ArrowRight" && !nextBtnDisabled) {
            event.stopPropagation();
            handleNextClick();
          } else if (event.key === "ArrowLeft" && !prevBtnDisabled) {
            event.stopPropagation();
            handlePrevClick();
          }
        } else {
          if (event.key === "ArrowDown" && !nextBtnDisabled) {
            event.stopPropagation();
            handleNextClick();
          } else if (event.key === "ArrowUp" && !prevBtnDisabled) {
            event.stopPropagation();
            handlePrevClick();
          }
        }
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    handleNextClick,
    handlePrevClick,
    prevBtnDisabled,
    nextBtnDisabled,
    isMobile,
    isFullscreen,
    mediaType,
    togglePlayPause,
  ]);

  // Handle trackpad/mouse wheel scrolling for image navigation
  useEffect(() => {
    if (mediaType === "video") return;

    const sliderElement = wheelContainerRef.current;
    if (!sliderElement) return;

    let scrollTimeout: NodeJS.Timeout | null = null;
    let accumulatedDelta = 0;
    const SCROLL_THRESHOLD = 50; // Adjust sensitivity

    function handleWheel(event: WheelEvent) {
      // Only handle vertical scrolling for images on desktop, horizontal on mobile
      const delta = isMobile ? event.deltaX : event.deltaY;

      // Ignore very small deltas (minor movements)
      if (Math.abs(delta) < 5) return;

      event.preventDefault();
      event.stopPropagation();

      accumulatedDelta += delta;

      // Clear existing timeout
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      // Check if we've scrolled enough to trigger navigation
      if (Math.abs(accumulatedDelta) >= SCROLL_THRESHOLD) {
        if (accumulatedDelta > 0 && !nextBtnDisabled) {
          handleNextClick();
        } else if (accumulatedDelta < 0 && !prevBtnDisabled) {
          handlePrevClick();
        }
        accumulatedDelta = 0;
      }

      // Reset accumulated delta after a brief pause
      scrollTimeout = setTimeout(() => {
        accumulatedDelta = 0;
      }, 150);
    }

    sliderElement.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      sliderElement.removeEventListener("wheel", handleWheel);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [
    mediaType,
    isMobile,
    handleNextClick,
    handlePrevClick,
    nextBtnDisabled,
    prevBtnDisabled,
  ]);

  // Handle fullscreen mode - request fullscreen on normal container
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    const normalContainer = normalContainerRef.current;

    if (isFullscreen && normalContainer) {
      document.body.style.overflow = "hidden";

      normalContainer.requestFullscreen?.().catch((err) => {
        console.error("Error entering fullscreen:", err);
      });

      document.addEventListener("fullscreenchange", handleFullscreenChange);
    } else {
      document.body.style.overflow = "";

      if (document.fullscreenElement) {
        document.exitFullscreen().catch((err) => {
          console.error("Error exiting fullscreen:", err);
        });
      }
    }

    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [isFullscreen]);
  // Handle fullscreen mode - move video element between containers
  // useEffect(() => {
  //   const video = videoRef.current;
  //   if (!video || mediaType !== "video") return;

  //   const handleFullscreenChange = () => {
  //     // If user exits fullscreen using ESC or browser button, update our state
  //     if (!document.fullscreenElement && isFullscreen) {
  //       setIsFullscreen(false);
  //     }
  //   };

  //   if (isFullscreen) {
  //     document.body.style.overflow = "hidden";

  //     // Move video to fullscreen container
  //     const fullscreenContainer = fullscreenContainerRef.current;
  //     if (fullscreenContainer && !fullscreenContainer.contains(video)) {
  //       fullscreenContainer.appendChild(video);

  //       // Enter native fullscreen
  //       fullscreenContainer.parentElement
  //         ?.requestFullscreen?.()
  //         .catch((err) => {
  //           console.error("Error entering fullscreen:", err);
  //         });
  //     }

  //     document.addEventListener("fullscreenchange", handleFullscreenChange);
  //   } else {
  //     document.body.style.overflow = "";

  //     // Exit native fullscreen if we're in it
  //     if (document.fullscreenElement) {
  //       document.exitFullscreen().catch((err) => {
  //         console.error("Error exiting fullscreen:", err);
  //       });
  //     }

  //     // Move video back to normal container
  //     const normalContainer = normalContainerRef.current;
  //     if (normalContainer && !normalContainer.contains(video)) {
  //       normalContainer.appendChild(video);
  //     }
  //   }

  //   return () => {
  //     document.body.style.overflow = "";
  //     document.removeEventListener("fullscreenchange", handleFullscreenChange);
  //   };
  // }, [isFullscreen, mediaType]);

  // Move video element between containers when fullscreen toggles
  // Removed problematic video moving logic that was causing React crashes
  // The video now stays in its original container and fullscreen is handled via CSS/Portal

  const handleThumbnailClick = (index: number) => {
    if (emblaApi) {
      emblaApi.scrollTo(index);
    }
  };

  const startAnimationLoop = useCallback(() => {
    const updateTime = () => {
      const video = videoRef.current;
      if (video && !video.paused && !video.ended) {
        setVideoState((prev) => ({
          ...prev,
          currentTime: video.currentTime,
          duration:
            video.duration && isFinite(video.duration)
              ? video.duration
              : prev.duration || 0,
        }));
        animationFrameRef.current = requestAnimationFrame(updateTime);
      }
    };
    animationFrameRef.current = requestAnimationFrame(updateTime);
  }, []);

  const stopAnimationLoop = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Update video width on window resize
  useEffect(() => {
    const updateVideoWidth = () => {
      const video = videoRef.current;
      if (video && video.offsetWidth > 0) {
        setVideoWidth(video.offsetWidth);
      }
    };

    // Initial update
    updateVideoWidth();

    window.addEventListener("resize", updateVideoWidth);
    return () => window.removeEventListener("resize", updateVideoWidth);
  }, []);

  // Hide timeline after inactivity
  const resetActivityTimeout = useCallback(() => {
    setIsTimelineVisible(true);
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    activityTimeoutRef.current = setTimeout(() => {
      // Don't hide if hovering over controls or volume slider is visible
      if (!isHoveringControls && !showVolumeSlider) {
        setIsTimelineVisible(false);
      }
    }, 3000);
  }, [isHoveringControls, showVolumeSlider]);

  useEffect(() => {
    if (!isFullscreen) {
      setIsTimelineVisible(true);
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
      return;
    }

    const handleActivity = () => resetActivityTimeout();

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("click", handleActivity);
    window.addEventListener("keydown", handleActivity);

    // Start the timeout initially
    resetActivityTimeout();

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("click", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
    };
  }, [isFullscreen, resetActivityTimeout]);

  const setVideoRefCallback = (el: HTMLVideoElement | null) => {
    if (el && !videoRef.current) {
      videoRef.current = el;

      setVideoState({
        duration: 0,
        currentTime: 0,
        isPlaying: false,
        volume: 1,
        isMuted: false,
      });

      // Eagerly start loading metadata - this makes the browser start fetching immediately
      el.load();

      const handleLoadedMetadata = () => {
        if (el.duration && isFinite(el.duration)) {
          setVideoState((prev) => ({
            ...prev,
            duration: el.duration,
          }));
        }
      };

      const handleDurationChange = () => {
        if (el.duration && isFinite(el.duration)) {
          setVideoState((prev) => ({
            ...prev,
            duration: el.duration,
          }));
        }
      };

      const handleCanPlay = () => {
        if (el.duration && isFinite(el.duration)) {
          setVideoState((prev) => ({
            ...prev,
            duration: el.duration,
          }));
        }
      };

      el.addEventListener("loadedmetadata", handleLoadedMetadata);
      el.addEventListener("durationchange", handleDurationChange);
      el.addEventListener("canplay", handleCanPlay);

      setTimeout(() => {
        if (!isFinite(el.duration)) {
          el.currentTime = 1e101;
          const seeked = () => {
            if (el.currentTime > 0 && isFinite(el.duration)) {
              setVideoState((prev) => ({
                ...prev,
                duration: el.duration,
              }));
              el.currentTime = 0;
              el.removeEventListener("timeupdate", seeked);
            }
          };
          el.addEventListener("timeupdate", seeked);
        }
      }, 1000);
    }
  };

  const handleVideoLoadedMetadata = () => {
    const video = videoRef.current;
    if (
      video &&
      video.duration &&
      !isNaN(video.duration) &&
      isFinite(video.duration)
    ) {
      setVideoState((prev) => ({
        ...prev,
        duration: video.duration,
      }));
    }
  };

  const handleDurationChange = () => {
    const video = videoRef.current;
    if (
      video &&
      video.duration &&
      !isNaN(video.duration) &&
      isFinite(video.duration)
    ) {
      setVideoState((prev) => ({
        ...prev,
        duration: video.duration,
      }));
    }
  };

  const handleVideoTimeUpdate = () => {
    const video = videoRef.current;
    if (video && !isDraggingPlayhead) {
      setVideoState((prev) => ({
        ...prev,
        currentTime: video.currentTime,
      }));
    }
  };

  const handleVideoPlay = () => {
    setVideoState((prev) => ({
      ...prev,
      isPlaying: true,
    }));
    startAnimationLoop();
  };

  const handleVideoPause = () => {
    setVideoState((prev) => ({
      ...prev,
      isPlaying: false,
    }));
    stopAnimationLoop();
  };

  const handleVideoEnded = () => {
    setVideoState((prev) => ({
      ...prev,
      isPlaying: false,
    }));
    stopAnimationLoop();
  };

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    if (!video) return;

    const timeline = e.currentTarget;
    const rect = timeline.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * video.duration;

    if (isFinite(newTime)) {
      video.currentTime = newTime;
      setVideoState((prev) => ({
        ...prev,
        currentTime: newTime,
      }));
    }
  };

  const handlePlayheadMouseDown = (
    e: React.MouseEvent,
    isFullscreenMode: boolean = false,
  ): void => {
    e.preventDefault();
    e.stopPropagation();

    const video = videoRef.current;
    if (!video) return;

    const wasPlaying = videoState.isPlaying;
    if (wasPlaying) {
      video.pause();
    }

    setIsDraggingPlayhead(true);

    const timelineSelector = isFullscreenMode
      ? `[data-timeline-fullscreen]`
      : `[data-timeline]`;

    const handleMouseMove = (moveEvent: MouseEvent): void => {
      const timeline = document.querySelector(timelineSelector) as HTMLElement;
      if (!timeline) return;

      const rect = timeline.getBoundingClientRect();
      const x = Math.max(
        0,
        Math.min(moveEvent.clientX - rect.left, rect.width),
      );
      const percentage = x / rect.width;
      const newTime = percentage * video.duration;

      if (isFinite(newTime)) {
        video.currentTime = newTime;
        setVideoState((prev) => ({
          ...prev,
          currentTime: newTime,
        }));
      }
    };

    const handleMouseUp = (): void => {
      setIsDraggingPlayhead(false);
      if (wasPlaying) {
        video.play().catch(console.error);
      }
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const pauseAllVideos = useCallback(() => {
    const video = videoRef.current;
    if (video && !video.paused) {
      video.pause();
    }
  }, []);

  const formatTime = (seconds: number): string => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  const getProgressPercentage = (): number => {
    if (!videoState.duration || !isFinite(videoState.duration)) return 0;
    return Math.min(100, (videoState.currentTime / videoState.duration) * 100);
  };

  useEffect(() => {
    const handleSeekToTimestamp = (e: CustomEvent) => {
      const { seconds } = e.detail;

      const video = videoRef.current;
      if (video) {
        video.currentTime = seconds;

        setVideoState((prev) => ({
          ...prev,
          currentTime: seconds,
        }));

        if (video.paused) {
          video.play().catch((err) => {
            console.error("Error playing video:", err);
          });
        }
      }
    };

    window.addEventListener(
      "seekToTimestamp",
      handleSeekToTimestamp as EventListener,
    );

    return () => {
      window.removeEventListener(
        "seekToTimestamp",
        handleSeekToTimestamp as EventListener,
      );
    };
  }, []);

  const currentSlide = slides[currentIndex];
  const currentProgress = getProgressPercentage();

  // Handle fullscreen timeline click
  const handleFullscreenTimelineClick = (
    e: React.MouseEvent<HTMLDivElement>,
  ) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;

    const timeline = e.currentTarget;
    const rect = timeline.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * video.duration;

    if (isFinite(newTime)) {
      video.currentTime = newTime;
      setVideoState((prev) => ({
        ...prev,
        currentTime: newTime,
      }));
    }
  };

  // With preload="metadata", allow playback as soon as video can play (has enough data to start)
  // canPlay event means browser has enough data AND duration is known
  // Also require valid duration before allowing playback
  const hasDuration = videoState.duration > 0 && isFinite(videoState.duration);
  const isLoading = !canPlay || isWaitingForData || !hasDuration;
  const showLoader =
    (videoLoaded && !canPlay) || isWaitingForData || !hasDuration;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || mediaType !== "video") return;

    const handleVideoClick = (e: MouseEvent) => {
      if (isFullscreen) {
        e.stopPropagation();
        if (!isLoading) togglePlayPause();
      }
    };

    const handleVideoDoubleClick = (e: MouseEvent) => {
      if (isFullscreen) {
        e.stopPropagation();
        if (!isLoading) setIsFullscreen(false);
      }
    };

    video.addEventListener("click", handleVideoClick);
    video.addEventListener("dblclick", handleVideoDoubleClick);

    return () => {
      video.removeEventListener("click", handleVideoClick);
      video.removeEventListener("dblclick", handleVideoDoubleClick);
    };
  }, [isFullscreen, isLoading, togglePlayPause, mediaType]);

  return (
    <section className="flex gap-5 lg:gap-0 flex-col-reverse lg:flex-row max-w-full h-full m-auto relative">
      {/* Fullscreen for images - use MediaSliderFull component */}
      {isFullscreen && (mediaType === "image" || mediaType === "comments") && (
        <AnimatePresence>
          <MediaSliderFull
            slides={fullscreenSlides}
            options={options}
            currentIndex={fullscreenStartIndex}
            onClose={() => setIsFullscreen(false)}
            mediaType={mediaType}
            report={report}
            onOpenNewImages={handleOpenNewImages}
          />
        </AnimatePresence>
      )}

      {/* Fullscreen Overlay for videos */}

      {mediaType !== "video" && (
        <div className="flex items-center justify-center text-sm font-semibold font-[Onest]">
          <div className="flex lg:flex-col gap-[12px] items-center lg:w-[81px] lg:overflow-x-visible">
            {slides.map((slide, index) => {
              return (
                <div
                  key={index}
                  onClick={() => handleThumbnailClick(index)}
                  className={`cursor-pointer shrink-0 ${
                    index === currentIndex ? "active" : ""
                  }`}
                >
                  {slide.mediaType === "image" ? (
                    <img
                      src={slide.url}
                      className={`${
                        currentIndex === index ? "image-shadow" : "shadow-none"
                      } transition-all border-[3px] border-transparent rounded-[12px] shrink-0 w-[76px] h-[56px] object-cover ${
                        currentIndex === index ? "opacity-100" : "opacity-60"
                      }`}
                    />
                  ) : (
                    <video
                      src={slide.url}
                      className={`rounded-[12px] shrink-0 w-[76px] ${
                        currentIndex === index ? "image-shadow" : "shadow-none"
                      } h-[56px] border-[3px] border-transparent object-cover ${
                        currentIndex === index ? "opacity-100" : "opacity-60"
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {mediaType === "video" ? (
        // Video rendered directly without Embla carousel
        <div className="flex flex-col justify-center items-center px-0 lg:px-[56px] w-full">
          {currentSlide &&
            (() => {
              const progress = getProgressPercentage();

              return (
                <div
                  key={currentIndex}
                  className="w-full flex flex-col items-center gap-5 lg:gap-4"
                >
                  <div className="w-full relative flex items-center justify-center">
                    <div
                      className="relative"
                      ref={normalContainerRef}
                      style={{
                        backgroundColor: isFullscreen ? "#000" : "transparent",
                        display: isFullscreen ? "flex" : "block",
                        alignItems: isFullscreen ? "center" : "initial",
                        justifyContent: isFullscreen ? "center" : "initial",
                        width: isFullscreen ? "100vw" : "auto",
                        height: isFullscreen ? "100vh" : "auto",
                      }}
                    >
                      {!isLoading &&
                        !videoState.isPlaying &&
                        videoState.currentTime === 0 && (
                          <>
                            {/* Dark overlay - now matches video size exactly */}
                            <div className="pointer-events-none absolute inset-0 bg-black/10 rounded-lg z-[1]" />

                            {/* Play button */}
                            <div className="pointer-events-none flex items-center justify-center w-[48px] h-[48px] rounded-[16px] bg-white absolute top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%] z-[2]">
                              <img src={PlayBlack} />
                            </div>
                          </>
                        )}

                      <video
                        ref={setVideoRefCallback}
                        data-video-element
                        src={currentSlide.url}
                        controls={false}
                        style={{
                          maxWidth: isFullscreen ? "100vw" : "100%",
                          maxHeight: isFullscreen
                            ? "100vh"
                            : "calc(100vh - 200px)",
                          width: isFullscreen ? "100vw" : "auto",
                          height: isFullscreen ? "100vh" : "auto",
                          borderRadius: isFullscreen ? "0" : "",
                        }}
                        className="rounded-lg shadow-md cursor-pointer object-contain"
                        autoPlay={false}
                        preload="auto"
                        muted={false}
                        playsInline
                        crossOrigin="anonymous"
                        onError={(e) => {
                          console.error(
                            "❌ Video failed to load:",
                            currentSlide.url,
                          );
                          console.error(
                            "Error details:",
                            e.currentTarget.error,
                          );
                          console.error(
                            "Error code:",
                            e.currentTarget.error?.code,
                          );
                          console.error(
                            "Error message:",
                            e.currentTarget.error?.message,
                          );
                        }}
                        onLoadedData={(e) => {
                          setVideoLoaded(true);
                          const video = e.currentTarget;
                          // Use setTimeout to ensure layout is complete
                          setTimeout(() => {
                            if (video.offsetWidth > 0) {
                              setVideoWidth(video.offsetWidth);
                            }
                          }, 0);

                          // Also observe resize
                          const resizeObserver = new ResizeObserver(() => {
                            if (video.offsetWidth > 0) {
                              setVideoWidth(video.offsetWidth);
                            }
                          });
                          resizeObserver.observe(video);

                          return () => resizeObserver.disconnect();
                        }}
                        onLoadedMetadata={(e) => {
                          handleVideoLoadedMetadata();
                          const video = e.currentTarget;
                          // Enable playback as soon as metadata is loaded with some buffer
                          if (
                            video.buffered.length > 0 &&
                            video.buffered.end(0) > 0
                          ) {
                            setCanPlay(true);
                          }
                        }}
                        onDurationChange={handleDurationChange}
                        onTimeUpdate={handleVideoTimeUpdate}
                        onPlay={handleVideoPlay}
                        onPause={handleVideoPause}
                        onEnded={handleVideoEnded}
                        onCanPlay={() => {
                          setCanPlay(true);
                        }}
                        onCanPlayThrough={() => {}}
                        onWaiting={() => {
                          setIsWaitingForData(true);
                        }}
                        onPlaying={() => {
                          setIsWaitingForData(false);
                        }}
                        onSeeked={() => {
                          setIsWaitingForData(false);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isLoading) togglePlayPause();
                        }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          if (!isLoading) {
                            if (isFullscreen) {
                              setIsFullscreen(false);
                            } else {
                              setIsFullscreen(true);
                            }
                          }
                        }}
                      />
                      {/* Fullscreen controls overlay */}
                      {isFullscreen && !isLoading && (
                        <>
                          <div
                            className="absolute left-1/2 -translate-x-1/2 w-[calc(100%-32px)] z-10 flex flex-col gap-4 transition-all duration-300"
                            style={{
                              bottom: isTimelineVisible ? "16px" : "-40px",
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onMouseEnter={() => {
                              setIsHoveringControls(true);
                              setIsTimelineVisible(true);
                              if (activityTimeoutRef.current) {
                                clearTimeout(activityTimeoutRef.current);
                              }
                            }}
                            onMouseLeave={(e) => {
                              const relatedTarget =
                                e.relatedTarget as HTMLElement;
                              const volumeSlider = relatedTarget?.closest(
                                "[data-volume-slider]",
                              );

                              if (!volumeSlider && !showVolumeSlider) {
                                setIsHoveringControls(false);
                                resetActivityTimeout();
                              }
                            }}
                          >
                            {/* Timeline */}
                            <div className="relative flex items-center">
                              <div
                                data-timeline-fullscreen
                                className="group w-full h-[12px] rounded-[6px] backdrop-blur-md relative cursor-pointer bg-white/[0.08] overflow-hidden"
                                onClick={handleTimelineClick}
                                onMouseMove={(e) => {
                                  const rect =
                                    e.currentTarget.getBoundingClientRect();
                                  const x = e.clientX - rect.left;
                                  const percentage = (x / rect.width) * 100;
                                  const video = videoRef.current;
                                  const time = video
                                    ? (percentage / 100) * video.duration
                                    : 0;
                                  setHoverPosition({
                                    percentage,
                                    time: isFinite(time) ? time : 0,
                                  });
                                }}
                                onMouseLeave={() => setHoverPosition(null)}
                              >
                                <div
                                  className="absolute top-0 left-0 h-full bg-white pointer-events-none"
                                  style={{
                                    width: `${getProgressPercentage()}%`,
                                  }}
                                />
                              </div>
                              <AnimatePresence>
                                {hoverPosition && (
                                  <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.15 }}
                                    className="absolute top-[-30px] h-5 rounded-lg px-1.5 backdrop-blur-[10px] bg-gray-300 border border-gray-400/[0.64] text-white text-xs flex items-center justify-center whitespace-nowrap z-[5] pointer-events-none"
                                    style={{
                                      left: `${hoverPosition.percentage}%`,
                                      transform: "translateX(-50%)",
                                    }}
                                  >
                                    {formatTime(hoverPosition.time)}
                                    <div
                                      className="absolute top-[25px] left-1/2 -translate-x-1/2 w-px h-6 bg-white rounded-[10px] pointer-events-none"
                                      style={{
                                        boxShadow:
                                          "0px 4px 4px 0px rgba(0, 0, 0, 0.2)",
                                      }}
                                    />
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>

                            {/* Controls */}
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  togglePlayPause();
                                }}
                                className="bg-white/[0.08] backdrop-blur-md flex items-center justify-center w-[40px] h-[40px] rounded-[12px] hover:bg-white/[0.16] transition-all cursor-pointer shrink-0"
                              >
                                {videoState.isPlaying ? (
                                  <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <path
                                      d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"
                                      fill="white"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <path d="M8 5v14l11-7z" fill="white" />
                                  </svg>
                                )}
                              </button>

                              <div
                                className="relative"
                                onMouseEnter={() => {
                                  setShowVolumeSlider(true);
                                  setIsTimelineVisible(true);
                                  if (activityTimeoutRef.current) {
                                    clearTimeout(activityTimeoutRef.current);
                                  }
                                }}
                                onMouseLeave={() => {
                                  setShowVolumeSlider(false);
                                  resetActivityTimeout();
                                }}
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleMute();
                                  }}
                                  className="bg-white/[0.08] backdrop-blur-md flex items-center justify-center w-[40px] h-[40px] rounded-[12px] hover:bg-white/[0.16] transition-all cursor-pointer shrink-0"
                                >
                                  {videoState.isMuted ||
                                  videoState.volume === 0 ? (
                                    <img src={MuteSm} alt="Muted" />
                                  ) : (
                                    <img src={Volume} alt="Volume" />
                                  )}
                                </button>
                                <AnimatePresence>
                                  {showVolumeSlider && (
                                    <motion.div
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      exit={{ opacity: 0 }}
                                      className="absolute bottom-full left-1/2 -translate-x-1/2 pb-2"
                                      data-volume-slider
                                    >
                                      <div
                                        style={{
                                          boxShadow:
                                            "0px 4px 24px rgba(0, 0, 0, 0.35)",
                                        }}
                                        className="rounded-[10px] w-[40px] h-[120px] flex flex-col items-center justify-end"
                                      >
                                        <div className="relative w-[40px] h-full bg-[#3D3D3D] rounded-[12px] overflow-hidden">
                                          <div
                                            className="absolute bottom-0 left-0 right-0 bg-white transition-[height] duration-75 ease-out"
                                            style={{
                                              height: `${
                                                videoState.volume * 100
                                              }%`,
                                            }}
                                          />
                                          <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            step="1"
                                            value={videoState.volume * 100}
                                            onInput={(e) => {
                                              e.stopPropagation();
                                              const video = videoRef.current;
                                              if (video) {
                                                const newVolume =
                                                  Number(
                                                    (
                                                      e.target as HTMLInputElement
                                                    ).value,
                                                  ) / 100;
                                                video.volume = newVolume;
                                                video.muted = newVolume === 0;
                                              }
                                            }}
                                            onChange={(e) => {
                                              e.stopPropagation();
                                              handleVolumeChange(
                                                Number(e.target.value) / 100,
                                              );
                                            }}
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                            style={{
                                              writingMode: "bt-lr" as any,
                                              WebkitAppearance:
                                                "slider-vertical" as any,
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>

                              <div className="flex-1" />

                              <div className="text-white/80 text-[14px] px-[10px] leading-[20px] backdrop-blur-md bg-white/[0.08] transition-all rounded-[12px] font-medium h-[40px] flex items-center">
                                {formatTime(videoState.currentTime)} /{" "}
                                {formatTime(videoState.duration)}
                              </div>

                              <button
                                onClick={() => setIsFullscreen(false)}
                                className="w-[40px] h-[40px] flex items-center justify-center cursor-pointer bg-white/[0.08] backdrop-blur-md rounded-[12px] hover:bg-white/[0.16] transition-all"
                              >
                                <img src={Resize} alt="Exit fullscreen" />
                              </button>
                            </div>
                          </div>

                          <AnimatePresence>
                            {isTimelineVisible && (
                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                style={{
                                  background:
                                    "linear-gradient(to bottom, transparent 0%, #0F0F0F 100%)",
                                }}
                                className="absolute h-[20%] bottom-0 left-0 pointer-events-none w-full"
                              ></motion.div>
                            )}
                          </AnimatePresence>
                        </>
                      )}

                      {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/10 z-[3]">
                          <img
                            src={VideoLoader}
                            className="w-[56px] h-[56px] animate-spin"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {canPlay && (
                    <div
                      className="flex items-center gap-2 shrink-0 min-w-0"
                      style={{
                        width: videoWidth ? `${videoWidth}px` : "100%",
                        maxWidth: "100%",
                      }}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePlayPause();
                        }}
                        className="bg-white/[0.08] backdrop-blur-md flex items-center justify-center w-[32px] h-[32px] rounded-[10px] hover:bg-white/[0.16] transition-all cursor-pointer shrink-0"
                      >
                        {videoState.isPlaying ? (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"
                              fill="white"
                            />
                          </svg>
                        ) : (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path d="M8 5v14l11-7z" fill="white" />
                          </svg>
                        )}
                      </button>

                      <div
                        className="relative"
                        onMouseEnter={() => setShowVolumeSlider(true)}
                        onMouseLeave={() => setShowVolumeSlider(false)}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleMute();
                          }}
                          className="bg-white/[0.08] backdrop-blur-md flex items-center justify-center w-[32px] h-[32px] rounded-[10px] hover:bg-white/[0.16] transition-all cursor-pointer shrink-0"
                        >
                          {videoState.isMuted || videoState.volume === 0 ? (
                            <img src={MuteSm} alt="Muted" />
                          ) : (
                            <img src={Volume} alt="Volume" />
                          )}
                        </button>
                        <AnimatePresence>
                          {showVolumeSlider && (
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              className="absolute bottom-full left-1/2 -translate-x-1/2 pb-2"
                            >
                              <div
                                style={{
                                  boxShadow: "0px 4px 24px rgba(0, 0, 0, 0.35)",
                                }}
                                className="rounded-[10px] w-[32px] h-[120px] flex flex-col items-center justify-end"
                              >
                                <div className="relative w-[32px] h-full bg-[#3D3D3D] rounded-[10px] overflow-hidden">
                                  <div
                                    className="absolute bottom-0 left-0 right-0 bg-white transition-[height] duration-75 ease-out"
                                    style={{
                                      height: `${videoState.volume * 100}%`,
                                    }}
                                  />
                                  <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    step="1"
                                    value={videoState.volume * 100}
                                    onInput={(e) => {
                                      e.stopPropagation();
                                      const video = videoRef.current;
                                      if (video) {
                                        const newVolume =
                                          Number(
                                            (e.target as HTMLInputElement)
                                              .value,
                                          ) / 100;
                                        video.volume = newVolume;
                                        video.muted = newVolume === 0;
                                      }
                                    }}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleVolumeChange(
                                        Number(e.target.value) / 100,
                                      );
                                    }}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    style={{
                                      writingMode: "bt-lr" as any,
                                      WebkitAppearance:
                                        "slider-vertical" as any,
                                    }}
                                  />
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      <div className="flex-1 relative flex items-center h-[32px]">
                        <div
                          data-timeline
                          className="group w-full h-[32px] rounded-[10px] relative cursor-pointer backdrop-blur-md bg-white/[0.08] overflow-hidden"
                          onClick={handleTimelineClick}
                          onMouseMove={(e) => {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            const x = e.clientX - rect.left;
                            const percentage = (x / rect.width) * 100;
                            const video = videoRef.current;
                            const time = video
                              ? (percentage / 100) * video.duration
                              : 0;
                            setHoverPosition({
                              percentage,
                              time: isFinite(time) ? time : 0,
                            });
                          }}
                          onMouseLeave={() => setHoverPosition(null)}
                        >
                          <div
                            className="absolute top-0 left-0 h-full bg-white pointer-events-none"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <AnimatePresence>
                          {hoverPosition && (
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 0.15 }}
                              className="absolute top-[-30px] h-5 rounded-lg px-1.5 bg-gray-300 border border-gray-400/[0.64] text-white text-xs flex items-center justify-center whitespace-nowrap z-[5] pointer-events-none"
                              style={{
                                left: `${hoverPosition.percentage}%`,
                                transform: "translateX(-50%)",
                              }}
                            >
                              {formatTime(hoverPosition.time)}
                              <div
                                className="absolute top-[25px] left-1/2 -translate-x-1/2 w-px h-10 bg-white rounded-[10px] pointer-events-none"
                                style={{
                                  boxShadow:
                                    "0px 4px 4px 0px rgba(0, 0, 0, 0.2)",
                                }}
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      <div className="w-[98px] backdrop-blur-md text-white/80 text-[14px] px-[10px] leading-[20px] bg-white/[0.08] transition-all rounded-[10px] font-medium h-[32px] flex justify-center items-center shrink-0 whitespace-nowrap overflow-hidden">
                        <span className="truncate">
                          {formatTime(videoState.currentTime)} /{" "}
                          {formatTime(videoState.duration)}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setIsFullscreen(true);
                        }}
                        className="bg-white/[0.08] hover:bg-white/[0.16] transition-all w-[32px] h-[32px] rounded-[10px] flex items-center justify-center cursor-pointer shrink-0"
                      >
                        <img src={Resize} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
        </div>
      ) : (
        // Image slider with Embla carousel
        <div
          className="overflow-hidden flex flex-col justify-center px-0 lg:px-[56px] w-full"
          ref={(node) => {
            emblaRef(node);
            wheelContainerRef.current = node;
          }}
        >
          <div
            className={`flex ${
              isMobile ? "flex-row" : "flex-col"
            } touch-pan-x touch-pinch-zoom h-full`}
            style={{
              marginTop: isMobile ? "0" : "calc(var(--slide-spacing) * -1)",
              marginLeft: isMobile ? "calc(var(--slide-spacing) * -1)" : "0",
            }}
            ref={sliderRef}
          >
            {slides.map((slide, index) => {
              // Determine comments for this slide
              const getCommentsForSlide = () => {
                // For "comments" mediaType, use elementComments (array) or elementComment from the slide
                if (mediaType === "comments") {
                  // Handle multiple comments per slide (new format)
                  if (
                    slide.elementComments &&
                    slide.elementComments.length > 0
                  ) {
                    return slide.elementComments
                      .map((ec, commentIndex) => {
                        // Prefer percentage values if available (more accurate)
                        if (
                          ec.screenshotXPercent != null &&
                          ec.screenshotYPercent != null
                        ) {
                          return {
                            id: ec.commentNumber || commentIndex + 1,
                            percentX: ec.screenshotXPercent,
                            percentY: ec.screenshotYPercent,
                            text: ec.text || "",
                            username: ec.username,
                            images: ec.images,
                            profilePicture: ec.profilePicture != null ? profilePictures[ec.profilePicture - 1] : undefined,
                          };
                        }
                        // Fall back to pixel coordinates if percentage not available
                        if (ec.screenshotX != null && ec.screenshotY != null) {
                          return {
                            id: ec.commentNumber || commentIndex + 1,
                            percentX: 0, // Will be overridden by pixelX
                            percentY: 0, // Will be overridden by pixelY
                            pixelX: ec.screenshotX,
                            pixelY: ec.screenshotY,
                            text: ec.text || "",
                            username: ec.username,
                            images: ec.images,
                            profilePicture: ec.profilePicture != null ? profilePictures[ec.profilePicture - 1] : undefined,
                          };
                        }
                        return null;
                      })
                      .filter(Boolean);
                  }
                  // Handle single comment per slide (legacy format)
                  if (slide.elementComment) {
                    const ec = slide.elementComment;
                    // Prefer percentage values if available (more accurate)
                    if (
                      ec.screenshotXPercent != null &&
                      ec.screenshotYPercent != null
                    ) {
                      return [
                        {
                          id: ec.commentNumber || index + 1,
                          percentX: ec.screenshotXPercent,
                          percentY: ec.screenshotYPercent,
                          text: ec.text || "",
                          username: ec.username,
                          images: ec.images,
                          profilePicture: ec.profilePicture != null ? profilePictures[ec.profilePicture - 1] : undefined,
                        },
                      ];
                    }
                    // Fall back to pixel coordinates if percentage not available
                    if (ec.screenshotX != null && ec.screenshotY != null) {
                      return [
                        {
                          id: ec.commentNumber || index + 1,
                          percentX: 0, // Will be overridden by pixelX
                          percentY: 0, // Will be overridden by pixelY
                          pixelX: ec.screenshotX,
                          pixelY: ec.screenshotY,
                          text: ec.text || "",
                          username: ec.username,
                          images: ec.images,
                          profilePicture: ec.profilePicture != null ? profilePictures[ec.profilePicture - 1] : undefined,
                        },
                      ];
                    }
                  }
                  return [];
                }
                // For "image" mediaType, use screenshotComments on first slide only
                if (mediaType === "image" && index === 0) {
                  return (report?.screenshotComments || []).map((sc: any) => ({
                    ...sc,
                    profilePicture: sc.profilePicture != null ? profilePictures[sc.profilePicture - 1] : undefined,
                  }));
                }
                return [];
              };

              return (
                <div
                  key={index}
                  className="w-full transform-gpu flex-[0_0_100%] flex items-center justify-center min-h-0 h-full"
                  style={{
                    paddingTop: isMobile ? "0" : "var(--slide-spacing)",
                    paddingLeft: isMobile ? "var(--slide-spacing)" : "0",
                  }}
                >
                  <div className="flex items-center justify-center h-full w-full">
                    <div className="flex flex-col justify-center items-center h-full w-full gap-4">
                      <div
                        className="max-h-[600px] cursor-pointer"
                        id="root-toolbar-container"
                      >
                        <ScreenshotWithComments
                          imageUrl={slide.url}
                          comments={getCommentsForSlide()}
                          alt={slide.filename || ""}
                          className="object-contain h-full w-full rounded-lg shadow-md"
                          style={{ maxHeight: "600px" }}
                          onClick={() => {
                            pauseAllVideos();
                            setFullscreenSlides(slides);
                            setFullscreenStartIndex(currentIndex);
                            setIsFullscreen(true);
                          }}
                          onImageClick={handleOpenNewImages}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};

export default MediaSlider;
